import axios from 'axios';
import { supabase } from '../../../../foundation/altzero_base/base/utils/supabaseClient';

const API_BASE_URL = '/api/scopingai/email';

export interface EmailAccount {
    id: string;
    email: string;
    is_active: boolean;
    is_default: boolean;
    created_at: string;
    updated_at?: string;
    expires_at?: number;
    user_id?: string;
    email_signature?: string | null;
    /** Populated by /org-accounts endpoint */
    owner_name?: string | null;
    /** True if this account belongs to the current user — populated by /org-accounts */
    is_own?: boolean;
}

export interface EmailThread {
    id: string;
    tender_id: string;
    retailer_id: string;
    subject: string;
    gmail_thread_id: string;
    last_message_at: string;
    status: string;
    retailer?: {
        name: string;
        contact_email: string;
    };
    tender?: {
        opportunity_name: string;
    };
    messages?: { count: number }[];
}

export interface EmailMessage {
    id: string;
    thread_id: string;
    gmail_message_id: string;
    sender_email: string;
    recipient_emails: string[];
    subject: string;
    body_html: string;
    snippet: string;
    direction: 'inbound' | 'outbound';
    sent_at: string;
    received_at: string;
    account_id: string;
    attachments: EmailAttachment[];
}

export interface EmailAttachment {
    id: string;
    filename: string;
    mime_type: string;
    size_bytes: number;
    parsing_status: 'pending' | 'parsed' | 'failed';
    message_id?: string;
    email_id?: string; // Alias common in frontend
}

export interface UnmatchedEmail {
    id: string;
    org_id: string;
    account_id: string;
    gmail_message_id: string;
    gmail_thread_id: string | null;
    sender_email: string;
    sender_name: string | null;
    subject: string;
    body_text: string | null;
    snippet: string | null;
    received_at: string;
    attachment_count: number;
    attachments: { filename: string; mimeType: string; attachmentId: string; size: number }[];
    status: 'pending' | 'assigned' | 'ignored';
    assigned_tender_id: string | null;
    assigned_at: string | null;
    created_at: string;
}

export const emailApiService = {
    /**
     * Get the configured axios instance with auth headers
     */
    getAxios: async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;

        return axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'x-user-id': userId || '',
                'Content-Type': 'application/json'
            }
        });
    },

    /**
     * Connect a new Gmail account
     */
    connectAccount: async (orgId?: string, returnTo?: string): Promise<{ url: string }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/connect`, { orgId, returnTo });
        return response.data;
    },

    /**
     * List connected email accounts
     */
    getAccounts: async (): Promise<EmailAccount[]> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/accounts`);
        return response.data;
    },

    getMyAccounts: async (): Promise<EmailAccount[]> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/my-accounts`);
        return response.data;
    },

    /** All org accounts (active + inactive) with owner info — for the management page */
    getOrgAccounts: async (): Promise<EmailAccount[]> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/org-accounts`);
        return response.data;
    },

    /**
     * Disconnect an email account (soft - sets is_active to false)
     */
    disconnectAccount: async (accountId: string): Promise<void> => {
        const api = await emailApiService.getAxios();
        const response = await api.patch(`/accounts/${accountId}/disconnect`);
        return response.data;
    },

    /**
     * Remove/delete an email account permanently (hard delete)
     */
    removeAccount: async (accountId: string): Promise<void> => {
        const api = await emailApiService.getAxios();
        const response = await api.delete(`/accounts/${accountId}`);
        return response.data;
    },

    /**
     * Attempt to refresh the access token for an account using its stored refresh_token.
     * Does not require OAuth — fails fast if refresh_token is revoked.
     */
    refreshToken: async (accountId: string): Promise<{ success: boolean; error?: string }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/accounts/${accountId}/refresh-token`);
        return response.data;
    },

    /**
     * Reconnect an inactive email account (triggers OAuth flow)
     */
    reconnectAccount: async (accountId: string, orgId?: string): Promise<{ url: string }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/accounts/${accountId}/reconnect`, { orgId });
        return response.data;
    },

    /**
     * Fetch + save the Gmail signature for a connected account
     */
    getGmailSignature: async (accountId: string): Promise<{ signature: string; fromEmail: string; hasSignature: boolean }> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/gmail-signature/${accountId}`);
        return response.data;
    },

    /**
     * Send an email
     */
    sendEmail: async (data: {
        accountId: string;
        to: string[];
        cc?: string[];
        bcc?: string[];
        subject: string;
        bodyHtml: string;
        tenderId: string;
        retailerId?: string;
        attachments?: File[];
        siteDocumentPaths?: string;
    }): Promise<{ success: boolean; threadId: string }> => {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;

        const formData = new FormData();
        formData.append('accountId', data.accountId);
        data.to.forEach(email => formData.append('to', email));
        if (data.cc && data.cc.length > 0) {
            data.cc.forEach(email => formData.append('cc', email));
        }
        if (data.bcc && data.bcc.length > 0) {
            data.bcc.forEach(email => formData.append('bcc', email));
        }
        formData.append('subject', data.subject);
        formData.append('bodyHtml', data.bodyHtml);
        formData.append('tenderId', data.tenderId);
        if (data.retailerId) formData.append('retailerId', data.retailerId);
        if (data.siteDocumentPaths) formData.append('siteDocumentPaths', data.siteDocumentPaths);

        if (data.attachments) {
            data.attachments.forEach(file => {
                formData.append('attachments', file);
            });
        }

        // For FormData we need to create a specific instance or just use axios directly with headers
        const response = await axios.post(`${API_BASE_URL}/send`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'x-user-id': userId || ''
            }
        });
        return response.data;
    },

    /**
     * Poll for new emails
     */
    pollEmails: async (): Promise<{ processed: number }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/poll`);
        return response.data;
    },

    /**
     * Get threads for a tender or all threads
     */
    getThreads: async (tenderId?: string): Promise<EmailThread[]> => {
        const api = await emailApiService.getAxios();
        const url = tenderId ? `/threads/${tenderId}` : `/threads`;
        const response = await api.get(url);
        return response.data;
    },

    /**
     * Get messages in a thread
     */
    getMessages: async (threadId: string): Promise<EmailMessage[]> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/messages/${threadId}`);
        return response.data;
    },

    /**
     * Reply to a thread
     */
    replyToThread: async (data: {
        accountId: string;
        threadId: string;
        to: string[];
        bodyHtml: string;
        attachments?: File[];
    }): Promise<void> => {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;

        const formData = new FormData();
        formData.append('accountId', data.accountId);
        formData.append('threadId', data.threadId);
        data.to.forEach(email => formData.append('to', email));
        formData.append('bodyHtml', data.bodyHtml);

        if (data.attachments) {
            data.attachments.forEach(file => {
                formData.append('attachments', file);
            });
        }

        const response = await axios.post(`${API_BASE_URL}/reply`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'x-user-id': userId || ''
            }
        });
        return response.data;
    },

    /**
     * Upload an offer file manually (no email needed)
     */
    uploadManualAttachment: async (file: File, tenderId: string, retailerId?: string): Promise<EmailAttachment> => {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('tenderId', tenderId);
        if (retailerId) formData.append('retailerId', retailerId);

        const response = await axios.post(`${API_BASE_URL}/manual-upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data', 'x-user-id': userId || '' }
        });
        return response.data.attachment;
    },

    /**
     * Parse an attachment
     */
    parseAttachment: async (attachmentId: string, accountId?: string): Promise<any> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/parse-attachment/${attachmentId}`, { accountId });
        return response.data;
    },

    /**
     * Create offer from parsed document
     */
    createOfferFromParsedDoc: async (
        parsedDocId: string,
        /** Refresh an offer row that already exists and create none — used by re-terming. */
        opts?: { updateOnly?: boolean },
    ): Promise<{ success: boolean; offerId: string }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/create-offer/${parsedDocId}`,
            opts?.updateOnly ? { updateOnly: true } : undefined);
        return response.data;
    },

    /**
     * Get email statistics
     */
    getStats: async (): Promise<{ accounts: number; inbox: number; sent: number }> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/stats`);
        return response.data;
    },

    /**
     * Get sent messages
     */
    getSentMessages: async (filters?: { tenderId?: string; clientId?: string }): Promise<EmailMessage[]> => {
        const api = await emailApiService.getAxios();
        const params = new URLSearchParams();
        if (filters?.tenderId) params.append('tenderId', filters.tenderId);
        if (filters?.clientId) params.append('clientId', filters.clientId);

        const response = await api.get(`/messages/sent?${params.toString()}`);
        return response.data;
    },

    /**
     * Get inbound messages (Retailer Replies)
     */
    getInboundMessages: async (filters?: { tenderId?: string; hasAttachments?: boolean; direction?: 'inbound' }): Promise<EmailMessage[]> => {
        const api = await emailApiService.getAxios();
        const params = new URLSearchParams();
        if (filters?.tenderId) params.append('tenderId', filters.tenderId);
        if (filters?.hasAttachments) params.append('hasAttachments', 'true');
        params.append('direction', 'inbound');

        const response = await api.get(`/messages/inbound?${params.toString()}`);
        return response.data;
    },

    /**
     * Download an attachment
     */
    downloadAttachment: async (attachmentId: string, accountId?: string): Promise<Blob> => {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;

        /**
         * ⚠️ THE REASON HAS TO REACH THE SCREEN, OR THE BUG CANNOT BE FOUND.
         *
         * This used to throw a flat 'Failed to download attachment' for every failure, and
         * Stage 3's viewer prints exactly what it is given. A missing session, a deleted
         * attachment row and a storage outage all arrived as the same sentence, so a tester
         * reporting "PDF preview not loading" handed us no way to tell which of them it was —
         * and every row in the database checks out, which is precisely when the status code is
         * the only evidence left.
         */
        if (!attachmentId) {
            throw new Error('This offer has no attachment linked to it, so there is nothing to preview.');
        }
        if (!userId) {
            throw new Error('Your session has expired. Sign in again to load the document.');
        }

        const query = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
        const response = await fetch(
            `${API_BASE_URL}/attachments/${encodeURIComponent(attachmentId)}/download${query}`,
            { headers: { 'x-user-id': userId } },
        );

        if (!response.ok) {
            // The route answers with { error } on every failure path; a proxy or gateway in
            // front of it may not, so fall back to the status text rather than losing both.
            const detail = await response.json().then(
                (body: any) => (typeof body?.error === 'string' ? body.error : ''),
                () => '',
            );
            throw new Error(
                `Could not load the document (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`
                + `${detail ? `: ${detail}` : ''}`,
            );
        }

        return await response.blob();
    },

    /**
     * Get attachment download URL
     */
    getAttachmentDownloadUrl: (attachmentId: string, accountId?: string): string => {
        return `${API_BASE_URL}/attachments/${attachmentId}/download?accountId=${accountId}`;
    },
    /**
     * Get email templates
     */
    getTemplates: async (category?: EmailTemplateCategory): Promise<EmailTemplate[]> => {
        const api = await emailApiService.getAxios();
        const params = category ? { category } : {};
        const response = await api.get(`/templates`, { params });
        return response.data;
    },

    createTemplate: async (data: { name: string; subject: string; body_html: string; is_default: boolean; category?: EmailTemplateCategory; attachments?: any[] }): Promise<EmailTemplate> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/templates`, data);
        return response.data;
    },

    /**
     * Update email template
     */
    updateTemplate: async (id: string, data: { name: string; subject: string; body_html: string; is_default: boolean; category?: EmailTemplateCategory; attachments?: any[] }): Promise<EmailTemplate> => {
        const api = await emailApiService.getAxios();
        const response = await api.put(`/templates/${id}`, data);
        return response.data;
    },

    /**
     * Delete email template
     */
    deleteTemplate: async (id: string): Promise<void> => {
        const api = await emailApiService.getAxios();
        const response = await api.delete(`/templates/${id}`);
        return response.data;
    },
    // Placeholders
    getPlaceholders: async () => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/placeholders`);
        return response.data;
    },

    createPlaceholder: async (data: any) => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/placeholders`, data);
        return response.data;
    },

    updatePlaceholder: async (id: string, data: any) => {
        const api = await emailApiService.getAxios();
        const response = await api.patch(`/placeholders/${id}`, data);
        return response.data;
    },

    deletePlaceholder: async (id: string) => {
        const api = await emailApiService.getAxios();
        const response = await api.delete(`/placeholders/${id}`);
        return response.data;
    },

    /**
     * Save parsed offer document to database
     */
    saveParsedDocument: async (data: {
        attachmentId?: string;
        tenderId: string;
        retailerId?: string;
        tenderSiteId?: string;
        parsedData: any;
        confidenceScore?: number;
        fileName?: string;
    }): Promise<{ success: boolean; id: string; data: any }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/parsed-documents`, data);
        return response.data;
    },

    /**
     * Get parsed documents for a tender
     */
    getParsedDocuments: async (tenderId: string): Promise<any[]> => {
        const api = await emailApiService.getAxios();
        const response = await api.get(`/parsed-documents/${tenderId}`);
        return response.data;
    },

    // ── Unmatched Inbox ────────────────────────────────────────────────────────

    /** Fetch pending unmatched emails for the user's org */
    getUnmatchedEmails: async (): Promise<UnmatchedEmail[]> => {
        const api = await emailApiService.getAxios();
        const response = await api.get('/unmatched');
        return response.data;
    },

    /** Assign an unmatched email to a tender (+ optional retailer) — creates a proper thread */
    assignUnmatchedEmail: async (id: string, tenderId: string, retailerId?: string): Promise<{ success: boolean; threadId: string }> => {
        const api = await emailApiService.getAxios();
        const response = await api.post(`/unmatched/${id}/assign`, { tenderId, retailerId });
        return response.data;
    },

    /** Dismiss / ignore an unmatched email */
    ignoreUnmatchedEmail: async (id: string): Promise<void> => {
        const api = await emailApiService.getAxios();
        await api.post(`/unmatched/${id}/ignore`);
    },

    /** Hard-delete a single unmatched email */
    deleteUnmatchedEmail: async (id: string): Promise<void> => {
        const api = await emailApiService.getAxios();
        await api.delete(`/unmatched/${id}`);
    },

    /** Hard-delete multiple unmatched emails in one request */
    bulkDeleteUnmatchedEmails: async (ids: string[]): Promise<void> => {
        const api = await emailApiService.getAxios();
        await api.delete('/unmatched/bulk', { data: { ids } });
    },
};

// `bill_validation` is the covering email that carries a Bill Validation report
// PDF. It is deliberately excluded from the two tender dialogs, which both ask
// for their own category explicitly — see the GET /templates handler.
export type EmailTemplateCategory = 'retailer_rfi' | 'client_proposal' | 'bill_validation' | 'general';

export interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    body_html: string;
    is_default: boolean;
    category: EmailTemplateCategory;
    user_id: string;
    org_id?: string;
    created_at: string;
    updated_at: string;
}
